import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Volume2, VolumeX, Pause, Play, 
  ZoomIn, ZoomOut, Home, Sparkles, Loader2, ImageIcon, Download,
  Edit3, Save, X, Music, Music2, Images, FileText, Award, MoreVertical, Users, Globe, Mail, Target, BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SpEdKitLogo from '../components/SpEdKitLogo';
import IEPGoalGenerator from '../components/IEPGoalGenerator';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEXT_SIZES = [
  { value: 1, label: '1x', class: 'text-xl sm:text-2xl' },
  { value: 1.5, label: '1.5x', class: 'text-2xl sm:text-3xl' },
  { value: 2, label: '2x', class: 'text-3xl sm:text-4xl' },
];

const VOICE_OPTIONS = [
  { value: 'nova', label: 'Nova', description: 'Energetic, upbeat' },
  { value: 'shimmer', label: 'Shimmer', description: 'Bright, cheerful' },
  { value: 'echo', label: 'Echo', description: 'Smooth, calm' },
  { value: 'fable', label: 'Fable', description: 'Storytelling' },
];

const BACKGROUND_MUSIC = [
  { id: 'none', label: 'No Music', url: null },
  { id: 'calm', label: 'Calm Piano', url: 'https://cdn.pixabay.com/audio/2024/11/10/audio_d1ceea46fa.mp3' },
  { id: 'nature', label: 'Nature Sounds', url: 'https://cdn.pixabay.com/audio/2022/02/23/audio_ea70ad08e3.mp3' },
  { id: 'lullaby', label: 'Soft Lullaby', url: 'https://cdn.pixabay.com/audio/2024/02/14/audio_48c3296d3b.mp3' },
];

const TIP_SHEET_LANGUAGES = [
  { id: 'english', label: 'English', flag: '🇺🇸' },
  { id: 'spanish', label: 'Español', flag: '🇪🇸' },
  { id: 'arabic', label: 'العربية', flag: '🇸🇦' },
  { id: 'chinese', label: '简体中文', flag: '🇨🇳' },
];

const StoryViewer = ({ user }) => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [textSize, setTextSize] = useState(1);
  
  // TTS State
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const audioRef = useRef(null);
  
  // Background music state
  const [selectedMusic, setSelectedMusic] = useState('none');
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicVolume, setMusicVolume] = useState(0.3);
  const musicRef = useRef(null);
  
  // Image generation state
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatingAllImages, setGeneratingAllImages] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  
  // Strategy visual, token board, and tip sheet state
  const [generatingTipSheet, setGeneratingTipSheet] = useState(false);
  const [generatingStrategyVisual, setGeneratingStrategyVisual] = useState(false);
  const [generatingTokenBoard, setGeneratingTokenBoard] = useState(false);
  
  // Email tip sheet dialog state
  const [emailTipSheetOpen, setEmailTipSheetOpen] = useState(false);
  const [emailTipSheetLang, setEmailTipSheetLang] = useState('english');
  const [emailForm, setEmailForm] = useState({ recipient_email: '', recipient_name: '', custom_message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // IEP Goal Generator state
  const [iepGoalOpen, setIepGoalOpen] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  // PDF export state
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // Desktop Visual Aid Preview state
  const [desktopAidPreviewOpen, setDesktopAidPreviewOpen] = useState(false);
  const [desktopAidData, setDesktopAidData] = useState(null);
  const [desktopAidImages, setDesktopAidImages] = useState([]);
  const [loadingDesktopAid, setLoadingDesktopAid] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(null);

  useEffect(() => {
    fetchStory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  // Cleanup music on unmount
  useEffect(() => {
    const musicElement = musicRef.current;
    return () => {
      if (musicElement) {
        musicElement.pause();
      }
    };
  }, []);

  const fetchStory = async () => {
    try {
      const response = await axios.get(`${API}/stories/${storyId}`, {
        withCredentials: true
      });
      setStory(response.data);
    } catch (error) {
      console.error('Error fetching story:', error);
      toast.error('Failed to load story');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < (story?.pages?.length || 0)) {
      // Cancel edit mode when changing pages
      if (isEditing) {
        setIsEditing(false);
        setEditedText('');
      }
      setCurrentPage(newPage);
      stopAudio();
    }
  };

  // ========== TTS Functions ==========
  const generateAudio = async () => {
    if (!story?.pages?.[currentPage]?.text) return;
    
    setLoadingAudio(true);
    try {
      const response = await axios.post(
        `${API}/stories/${storyId}/tts`,
        {
          text: story.pages[currentPage].text,
          voice: selectedVoice
        },
        { withCredentials: true }
      );
      
      setAudioSrc(response.data.audio_base64);
    } catch (error) {
      console.error('Error generating audio:', error);
      toast.error('Failed to generate audio');
    } finally {
      setLoadingAudio(false);
    }
  };

  const playAudio = async () => {
    if (!audioSrc) {
      await generateAudio();
    }
    
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
    setAudioSrc(null);
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  // ========== Background Music Functions ==========
  const handleMusicChange = (musicId) => {
    setSelectedMusic(musicId);
    
    if (musicRef.current) {
      musicRef.current.pause();
      setMusicPlaying(false);
    }
    
    const music = BACKGROUND_MUSIC.find(m => m.id === musicId);
    if (music?.url && musicRef.current) {
      musicRef.current.src = music.url;
      musicRef.current.volume = musicVolume;
      musicRef.current.loop = true;
    }
  };

  const toggleMusic = () => {
    if (!musicRef.current || selectedMusic === 'none') return;
    
    if (musicPlaying) {
      musicRef.current.pause();
      setMusicPlaying(false);
    } else {
      const music = BACKGROUND_MUSIC.find(m => m.id === selectedMusic);
      if (music?.url) {
        musicRef.current.src = music.url;
        musicRef.current.volume = musicVolume;
        musicRef.current.loop = true;
        musicRef.current.play();
        setMusicPlaying(true);
      }
    }
  };

  // ========== Image Generation ==========
  const generateImage = async () => {
    const pageNumber = currentPage + 1;
    setGeneratingImage(true);
    
    try {
      const response = await axios.post(
        `${API}/stories/${storyId}/generate-image/${pageNumber}`,
        {},
        { withCredentials: true }
      );
      
      setStory(prev => {
        const updatedPages = [...prev.pages];
        updatedPages[currentPage] = {
          ...updatedPages[currentPage],
          image_base64: response.data.image_base64
        };
        return { ...prev, pages: updatedPages };
      });
      
      toast.success('Image generated!');
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  // ========== Batch Image Generation ==========
  const generateAllImages = async () => {
    setGeneratingAllImages(true);
    setBatchProgress({ current: 0, total: story?.pages?.length || 0 });
    toast.info('Generating all images... This may take a few minutes.');
    
    try {
      const response = await axios.post(
        `${API}/stories/${storyId}/generate-all-images`,
        {},
        { withCredentials: true }
      );
      
      // Refresh story data
      const storyResponse = await axios.get(`${API}/stories/${storyId}`, {
        withCredentials: true
      });
      setStory(storyResponse.data);
      
      const result = response.data;
      toast.success(`Generated ${result.successful} images! (${result.skipped} skipped, ${result.failed} failed)`);
    } catch (error) {
      console.error('Error generating all images:', error);
      toast.error('Failed to generate images');
    } finally {
      setGeneratingAllImages(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  // ========== Desktop Visual Aid Preview ==========
  const openDesktopAidPreview = async () => {
    setDesktopAidPreviewOpen(true);
    setLoadingDesktopAid(true);
    setDesktopAidImages([null, null, null, null]);
    
    try {
      // First, get the strategy data
      const response = await axios.post(
        `${API}/stories/${storyId}/generate-strategy-visual`,
        {},
        { withCredentials: true }
      );
      
      const data = response.data;
      setDesktopAidData(data);
      
      const strategies = data.strategies || [];
      
      // Generate images for each strategy
      const newImages = [null, null, null, null];
      for (let i = 0; i < strategies.length && i < 4; i++) {
        const strategy = strategies[i];
        try {
          const imageResponse = await axios.post(
            `${API}/stories/${storyId}/generate-strategy-image`,
            null,
            { 
              withCredentials: true,
              params: { image_prompt: strategy.image_prompt || `A child ${strategy.phrase}` }
            }
          );
          newImages[i] = imageResponse.data.image_base64;
          setDesktopAidImages([...newImages]); // Update state to show progress
        } catch (imgError) {
          console.warn(`Failed to generate image for strategy ${i + 1}:`, imgError);
          newImages[i] = null;
        }
      }
      
      setDesktopAidImages(newImages);
      toast.success('Preview ready! Review images and download when satisfied.');
      
    } catch (error) {
      console.error('Error generating desktop visual aid:', error);
      toast.error('Failed to generate desktop visual aid');
      setDesktopAidPreviewOpen(false);
    } finally {
      setLoadingDesktopAid(false);
    }
  };

  // Regenerate a single strategy image
  const regenerateStrategyImage = async (index) => {
    if (!desktopAidData?.strategies?.[index]) return;
    
    setRegeneratingImage(index);
    try {
      const strategy = desktopAidData.strategies[index];
      const imageResponse = await axios.post(
        `${API}/stories/${storyId}/generate-strategy-image`,
        null,
        { 
          withCredentials: true,
          params: { image_prompt: strategy.image_prompt || `A child ${strategy.phrase}` }
        }
      );
      
      const newImages = [...desktopAidImages];
      newImages[index] = imageResponse.data.image_base64;
      setDesktopAidImages(newImages);
      toast.success('Image regenerated!');
    } catch (error) {
      console.error('Error regenerating image:', error);
      toast.error('Failed to regenerate image');
    } finally {
      setRegeneratingImage(null);
    }
  };

  // Download Desktop Visual Aid as PDF (from preview)
  const downloadDesktopAidPdf = () => {
    if (!desktopAidData) return;
    
    const data = desktopAidData;
    const strategies = data.strategies || [];
    
    // Create PDF - Portrait letter size for classroom display
    const pdf = new jsPDF('portrait', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    
    // Dark sage green color: RGB(58, 90, 64)
    const darkSage = [58, 90, 64];
    
    // White background
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    // Title at top - large, bold, dark sage
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(36);
    pdf.setTextColor(...darkSage);
    const title = (data.card_title || 'MY REMINDERS').toUpperCase();
    pdf.text(title, pageWidth / 2, margin + 15, { align: 'center' });
    
    // Student name subtitle
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(14);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`For: ${data.student_name}`, pageWidth / 2, margin + 25, { align: 'center' });
    
    // Calculate grid positions - 2x2 layout
    const gridTop = margin + 40;
    const gridWidth = (pageWidth - 3 * margin) / 2;
    const gridHeight = (pageHeight - gridTop - margin - 20) / 2;
    
    // Draw 4 strategy quadrants with images
    for (let index = 0; index < 4; index++) {
      const strategy = strategies[index] || { phrase: `Strategy ${index + 1}` };
      const col = index % 2;
      const row = Math.floor(index / 2);
      const x = margin + col * (gridWidth + margin);
      const y = gridTop + row * gridHeight;
      
      // Quadrant center
      const centerX = x + gridWidth / 2;
      
      // Light border around each quadrant
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(1);
      pdf.roundedRect(x, y, gridWidth, gridHeight - 5, 5, 5, 'S');
      
      // Strategy phrase at top of quadrant
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(...darkSage);
      const phrase = strategy.phrase || `Strategy ${index + 1}`;
      const phraseLines = pdf.splitTextToSize(phrase, gridWidth - 10);
      let phraseY = y + 12;
      phraseLines.forEach((line) => {
        pdf.text(line, centerX, phraseY, { align: 'center' });
        phraseY += 7;
      });
      
      // Add generated image or placeholder
      const imageY = phraseY + 5;
      const imageSize = Math.min(gridWidth - 20, gridHeight - 50);
      const imageX = centerX - imageSize / 2;
      
      if (desktopAidImages[index]) {
        try {
          pdf.addImage(desktopAidImages[index], 'PNG', imageX, imageY, imageSize, imageSize);
        } catch (imgErr) {
          // Draw placeholder rectangle if image fails
          pdf.setFillColor(245, 245, 245);
          pdf.roundedRect(imageX, imageY, imageSize, imageSize, 5, 5, 'F');
          pdf.setFontSize(10);
          pdf.setTextColor(150, 150, 150);
          pdf.text('Image', centerX, imageY + imageSize / 2, { align: 'center' });
        }
      } else {
        // Draw placeholder
        pdf.setFillColor(245, 245, 245);
        pdf.roundedRect(imageX, imageY, imageSize, imageSize, 5, 5, 'F');
        pdf.setFontSize(10);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Image', centerX, imageY + imageSize / 2, { align: 'center' });
      }
    }
    
    // Footer
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Created with SpEdKit', pageWidth / 2, pageHeight - margin, { align: 'center' });
    
    // Save
    pdf.save(`${data.student_name}_Desktop_Visual_Aid.pdf`);
    toast.success('Desktop Visual Aid downloaded!');
    setDesktopAidPreviewOpen(false);
  };

  // Legacy function for dropdown menu (now opens preview)
  const generateStrategyVisual = () => {
    openDesktopAidPreview();
  };

  // ========== Token Reinforcement Board PDF ==========
  const generateTokenBoard = async () => {
    setGeneratingTokenBoard(true);
    toast.info('Creating token board...');
    
    try {
      const response = await axios.get(
        `${API}/stories/${storyId}/token-board-data`,
        { withCredentials: true }
      );
      
      const data = response.data;
      
      // Create PDF - landscape A4
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      
      // Background colors based on theme
      const themeColors = {
        dinosaurs: { bg: [230, 243, 230], accent: [76, 175, 80], icon: '🦕' },
        space: { bg: [225, 230, 245], accent: [63, 81, 181], icon: '🚀' },
        vehicles: { bg: [255, 243, 224], accent: [255, 152, 0], icon: '🚗' },
        animals: { bg: [232, 245, 233], accent: [76, 175, 80], icon: '🐾' },
        ocean: { bg: [224, 247, 250], accent: [0, 151, 167], icon: '🐠' },
        superhero: { bg: [227, 242, 253], accent: [25, 118, 210], icon: '⭐' },
        fantasy: { bg: [252, 228, 236], accent: [233, 30, 99], icon: '✨' },
        gaming: { bg: [243, 229, 245], accent: [156, 39, 176], icon: '🎮' },
        music: { bg: [255, 249, 196], accent: [255, 193, 7], icon: '🎵' },
        sports: { bg: [255, 235, 238], accent: [244, 67, 54], icon: '⚽' },
        stars: { bg: [253, 251, 247], accent: [244, 162, 97], icon: '⭐' }
      };
      
      const theme = themeColors[data.background_theme] || themeColors.stars;
      
      // Fill background with theme color
      pdf.setFillColor(...theme.bg);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Decorative border with accent color
      pdf.setDrawColor(...theme.accent);
      pdf.setLineWidth(4);
      pdf.roundedRect(margin, margin, pageWidth - 2 * margin, pageHeight - 2 * margin, 10, 10, 'S');
      
      // Inner decorative line
      pdf.setLineWidth(1.5);
      pdf.roundedRect(margin + 5, margin + 5, pageWidth - 2 * margin - 10, pageHeight - 2 * margin - 10, 8, 8, 'S');
      
      // Header banner
      pdf.setFillColor(...theme.accent);
      pdf.roundedRect(margin + 15, margin + 12, pageWidth - 2 * margin - 30, 35, 6, 6, 'F');
      
      // Title text
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(32);
      pdf.setTextColor(255, 255, 255);
      pdf.text('I am working for:', pageWidth / 2, margin + 28, { align: 'center' });
      
      // Student name
      pdf.setFontSize(18);
      pdf.text(`${data.student_name}`, pageWidth / 2, margin + 40, { align: 'center' });
      
      // Simple goal in large, friendly text - centered with icon
      const goalY = margin + 70;
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin + 40, goalY - 15, pageWidth - 2 * margin - 80, 35, 8, 8, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(28);
      pdf.setTextColor(...theme.accent);
      
      // Add goal icon using emoji text
      const goalIcon = data.goal_icon === 'heart' ? '❤️' : 
                       data.goal_icon === 'smile' ? '😊' :
                       data.goal_icon === 'refresh' ? '🔄' :
                       data.goal_icon === 'pause' ? '⏸️' :
                       data.goal_icon === 'gift' ? '🎁' :
                       data.goal_icon === 'clock' ? '⏰' :
                       data.goal_icon === 'ear' ? '👂' :
                       data.goal_icon === 'check' ? '✅' : '⭐';
      
      pdf.text(`${goalIcon} ${data.simple_goal} ${goalIcon}`, pageWidth / 2, goalY + 5, { align: 'center' });
      
      // === 5 TOKEN CIRCLES - Large and centered ===
      const tokenCount = 5;  // Always 5 tokens
      const tokenSize = 32;  // Larger tokens
      const tokenSpacing = 15;
      const totalTokenWidth = (tokenCount * tokenSize) + ((tokenCount - 1) * tokenSpacing);
      const tokenStartX = (pageWidth - totalTokenWidth) / 2;
      const tokenY = margin + 115;
      
      for (let i = 0; i < tokenCount; i++) {
        const x = tokenStartX + (i * (tokenSize + tokenSpacing)) + tokenSize / 2;
        
        // Outer circle with shadow effect
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(...theme.accent);
        pdf.setLineWidth(3);
        pdf.circle(x, tokenY + tokenSize / 2, tokenSize / 2, 'FD');
        
        // Inner dashed circle for placing token/sticker
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(1);
        pdf.setLineDashPattern([2, 2], 0);
        pdf.circle(x, tokenY + tokenSize / 2, tokenSize / 2 - 5, 'S');
        pdf.setLineDashPattern([], 0);
        
        // Number below each token
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.setTextColor(...theme.accent);
        pdf.text(`${i + 1}`, x, tokenY + tokenSize + 8, { align: 'center' });
      }
      
      // Reward section at bottom
      const rewardY = tokenY + tokenSize + 25;
      pdf.setFillColor(...theme.accent);
      pdf.roundedRect(margin + 20, rewardY, pageWidth - 2 * margin - 40, 30, 6, 6, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.setTextColor(255, 255, 255);
      pdf.text('When I earn 5 tokens, I get my reward!', pageWidth / 2, rewardY + 12, { align: 'center' });
      
      // Reward options box
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin + 60, rewardY + 18, pageWidth - 2 * margin - 120, 14, 4, 4, 'F');
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(60, 60, 60);
      pdf.text('My reward: _______________________', pageWidth / 2, rewardY + 27, { align: 'center' });
      
      // Footer with interests decoration
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      const interestsText = data.interests?.length > 0 
        ? `Theme: ${data.interests.slice(0, 3).join(', ')}` 
        : '';
      pdf.text(`Created with SpEdKit ${interestsText}`, pageWidth / 2, pageHeight - 15, { align: 'center' });
      
      // Add theme icon decorations in corners
      pdf.setFontSize(24);
      pdf.text(theme.icon, margin + 20, margin + 25);
      pdf.text(theme.icon, pageWidth - margin - 20, margin + 25);
      pdf.text(theme.icon, margin + 20, pageHeight - margin - 10);
      pdf.text(theme.icon, pageWidth - margin - 20, pageHeight - margin - 10);
      
      // Save
      pdf.save(`${data.student_name}_Token_Board.pdf`);
      toast.success('Token board downloaded!');
      
    } catch (error) {
      console.error('Error generating token board:', error);
      toast.error('Failed to generate token board');
    } finally {
      setGeneratingTokenBoard(false);
    }
  };

  // ========== Parent Tip Sheet PDF ==========
  const generateParentTipSheet = async (language = 'english') => {
    setGeneratingTipSheet(true);
    toast.info(`Creating parent tip sheet in ${language}...`);
    
    try {
      const response = await axios.post(
        `${API}/stories/${storyId}/generate-parent-tipsheet`,
        { language },
        { withCredentials: true }
      );
      
      const data = response.data;
      const tipsheet = data.tipsheet;
      
      // Create PDF - Simple clean design with WHITE background and DARK SAGE text
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      
      // Dark sage green color for text: RGB(58, 90, 64)
      const darkSage = [58, 90, 64];
      
      // ===== PAGE SETUP - Pure white background =====
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // ===== HEADER =====
      let yPos = margin;
      
      // Title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(24);
      pdf.setTextColor(...darkSage);
      const titleText = tipsheet.title || 'Tips for Home';
      const titleLines = pdf.splitTextToSize(titleText, contentWidth);
      pdf.text(titleLines, margin, yPos + 8);
      yPos += titleLines.length * 10 + 5;
      
      // Subtitle with student name
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Supporting ${data.student_name} at home`, margin, yPos);
      yPos += 8;
      
      // Language indicator if not English
      const langLabel = TIP_SHEET_LANGUAGES.find(l => l.id === language);
      if (langLabel && language !== 'english') {
        pdf.setFontSize(10);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`(${langLabel.label})`, margin, yPos);
        yPos += 6;
      }
      
      // Subtle divider line
      yPos += 5;
      pdf.setDrawColor(...darkSage);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;
      
      // ===== INTRODUCTION =====
      if (tipsheet.introduction) {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(11);
        pdf.setTextColor(80, 80, 80);
        const introLines = pdf.splitTextToSize(tipsheet.introduction, contentWidth);
        pdf.text(introLines, margin, yPos);
        yPos += introLines.length * 5 + 10;
      }
      
      // ===== TIPS SECTION =====
      const tips = tipsheet.tips || [];
      
      tips.forEach((tip, index) => {
        // Check if we need a new page
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        // Tip number and title
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.setTextColor(...darkSage);
        const tipTitle = `${index + 1}. ${tip.title || `Tip ${index + 1}`}`;
        pdf.text(tipTitle, margin, yPos);
        yPos += 7;
        
        // Description
        if (tip.description) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          const descLines = pdf.splitTextToSize(tip.description, contentWidth);
          pdf.text(descLines, margin, yPos);
          yPos += descLines.length * 4.5 + 4;
        }
        
        // How-to steps
        const steps = (tip.how_to || []).slice(0, 4);
        if (steps.length > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.setTextColor(70, 70, 70);
          
          steps.forEach((step) => {
            const bulletX = margin + 5;
            const textX = margin + 12;
            
            // Simple bullet point
            pdf.setFillColor(...darkSage);
            pdf.circle(bulletX, yPos - 1, 1.2, 'F');
            
            const stepLines = pdf.splitTextToSize(step, contentWidth - 15);
            pdf.text(stepLines, textX, yPos);
            yPos += stepLines.length * 4 + 2;
          });
          yPos += 3;
        }
        
        // Example (if exists)
        if (tip.example) {
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(9);
          pdf.setTextColor(90, 90, 90);
          const exampleText = `Example: "${tip.example}"`;
          const exampleLines = pdf.splitTextToSize(exampleText, contentWidth - 10);
          pdf.text(exampleLines, margin + 5, yPos);
          yPos += exampleLines.length * 4 + 5;
        }
        
        yPos += 6; // Space between tips
      });
      
      // ===== ENCOURAGEMENT =====
      if (tipsheet.encouragement) {
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        yPos += 5;
        pdf.setDrawColor(...darkSage);
        pdf.setLineWidth(0.3);
        pdf.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(...darkSage);
        const encLines = pdf.splitTextToSize(tipsheet.encouragement, contentWidth);
        pdf.text(encLines, margin, yPos);
        yPos += encLines.length * 5 + 8;
      }
      
      // ===== WHEN TO SEEK HELP =====
      if (tipsheet.when_to_seek_help) {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(...darkSage);
        pdf.text('When to seek additional support:', margin, yPos);
        yPos += 5;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);
        const helpLines = pdf.splitTextToSize(tipsheet.when_to_seek_help, contentWidth);
        pdf.text(helpLines, margin, yPos);
      }
      
      // ===== FOOTER =====
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text('Created with SpEdKit | Supporting Every Learner', pageWidth / 2, pageHeight - 10, { align: 'center' });
      
      // Save PDF
      const langSuffix = language !== 'english' ? `_${language}` : '';
      pdf.save(`Parent_Tips_${data.student_name}${langSuffix}.pdf`);
      toast.success('Parent tip sheet downloaded!');
      
    } catch (error) {
      console.error('Error generating tip sheet:', error);
      toast.error('Failed to generate tip sheet');
    } finally {
      setGeneratingTipSheet(false);
    }
  };

  // ========== Email Tip Sheet ==========
  const sendEmailTipSheet = async () => {
    if (!emailForm.recipient_email) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingEmail(true);
    toast.info('Sending tip sheet...');
    
    try {
      await axios.post(
        `${API}/stories/${storyId}/email-tipsheet`,
        {
          recipient_email: emailForm.recipient_email,
          recipient_name: emailForm.recipient_name || null,
          language: emailTipSheetLang,
          custom_message: emailForm.custom_message || null
        },
        { withCredentials: true }
      );
      
      setEmailTipSheetOpen(false);
      setEmailForm({ recipient_email: '', recipient_name: '', custom_message: '' });
      toast.success('Tip sheet sent successfully!');
    } catch (error) {
      console.error('Error emailing tip sheet:', error);
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // ========== Edit Functions ==========
  const startEditing = () => {
    setEditedText(story?.pages?.[currentPage]?.text || '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedText('');
  };

  const saveEdit = async () => {
    if (!story) return;
    
    setSavingEdit(true);
    try {
      const updatedPages = story.pages.map((page, index) => {
        if (index === currentPage) {
          return { ...page, text: editedText };
        }
        return page;
      });
      
      await axios.put(
        `${API}/stories/${storyId}`,
        { pages: updatedPages },
        { withCredentials: true }
      );
      
      setStory(prev => ({ ...prev, pages: updatedPages }));
      setIsEditing(false);
      setEditedText('');
      setAudioSrc(null); // Clear cached audio since text changed
      toast.success('Story updated!');
    } catch (error) {
      console.error('Error saving edit:', error);
      toast.error('Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  // ========== PDF Export ==========
  const exportToPdf = async () => {
    if (!story) return;
    
    setExportingPdf(true);
    toast.info('Generating PDF...');
    
    try {
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      
      // Title page
      pdf.setFillColor(253, 251, 247); // Background color
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(32);
      pdf.setTextColor(45, 52, 54);
      
      const title = story.title || 'Social Story';
      const titleLines = pdf.splitTextToSize(title, pageWidth - 2 * margin);
      pdf.text(titleLines, pageWidth / 2, pageHeight / 3, { align: 'center' });
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(16);
      pdf.setTextColor(86, 93, 94);
      pdf.text(`A story for ${story.student_info?.name}`, pageWidth / 2, pageHeight / 2, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.text('Created with SpEdKit', pageWidth / 2, pageHeight - 30, { align: 'center' });
      
      // Story pages
      for (let i = 0; i < story.pages.length; i++) {
        const page = story.pages[i];
        pdf.addPage();
        
        // Background
        pdf.setFillColor(253, 251, 247);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        
        // Page number
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(138, 145, 146);
        pdf.text(`Page ${i + 1} of ${story.pages.length}`, pageWidth / 2, 15, { align: 'center' });
        
        // Image (if exists)
        if (page.image_base64) {
          try {
            const imgData = page.image_base64;
            const imgWidth = 100;
            const imgHeight = 75;
            const imgX = pageWidth - margin - imgWidth;
            const imgY = (pageHeight - imgHeight) / 2;
            pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth, imgHeight);
          } catch (imgError) {
            console.warn('Could not add image to PDF:', imgError);
          }
        }
        
        // Text
        const textWidth = page.image_base64 ? (pageWidth / 2) - margin : pageWidth - 2 * margin;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(18);
        pdf.setTextColor(45, 52, 54);
        
        const textLines = pdf.splitTextToSize(page.text, textWidth);
        const textY = (pageHeight - (textLines.length * 10)) / 2;
        pdf.text(textLines, margin, textY);
      }
      
      // Save
      const fileName = `${story.title?.replace(/[^a-z0-9]/gi, '_') || 'social_story'}.pdf`;
      pdf.save(fileName);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const getCurrentTextSizeClass = () => {
    return TEXT_SIZES.find(s => s.value === textSize)?.class || TEXT_SIZES[0].class;
  };

  const currentPageData = story?.pages?.[currentPage];
  const hasImage = currentPageData?.image_base64 || currentPageData?.image_url;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="font-body text-text-secondary mb-4">Story not found</p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="story-viewer-page">
      {/* Header */}
      <header className="w-full px-4 sm:px-6 py-4 bg-white shadow-soft z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-8 h-8 sm:w-10 sm:h-10" showText={true} textClassName="text-xl sm:text-2xl hidden sm:block" />
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Generate All Images Button */}
            <Button
              onClick={generateAllImages}
              disabled={generatingAllImages}
              variant="outline"
              className="border-2 border-accent text-accent hover:bg-accent/10 rounded-full px-3 py-1.5 text-sm"
              data-testid="generate-all-images-btn"
            >
              {generatingAllImages ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  <span className="hidden sm:inline">Generating...</span>
                </>
              ) : (
                <>
                  <Images className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">All Images</span>
                </>
              )}
            </Button>
            
            {/* Downloads Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full px-3 py-1.5 text-sm"
                  data-testid="downloads-dropdown-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Downloads</span>
                  <MoreVertical className="w-3 h-3 ml-1 sm:hidden" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={exportToPdf}
                  disabled={exportingPdf}
                  className="cursor-pointer"
                  data-testid="export-story-pdf-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  {exportingPdf ? 'Generating...' : 'Story PDF'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={generateStrategyVisual}
                  disabled={generatingStrategyVisual}
                  className="cursor-pointer"
                  data-testid="desktop-visual-aid-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {generatingStrategyVisual ? 'Generating...' : 'Desktop Visual Aid'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={generateTokenBoard}
                  disabled={generatingTokenBoard}
                  className="cursor-pointer"
                  data-testid="token-board-btn"
                >
                  <Award className="w-4 h-4 mr-2" />
                  {generatingTokenBoard ? 'Generating...' : 'Token Reward Board'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => setIepGoalOpen(true)}
                  className="cursor-pointer"
                  data-testid="iep-goal-btn"
                >
                  <Target className="w-4 h-4 mr-2" />
                  IEP Goal Generator
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger 
                    className="cursor-pointer"
                    disabled={generatingTipSheet}
                    data-testid="parent-tipsheet-btn"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {generatingTipSheet ? 'Generating...' : 'Parent Tip Sheet'}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {TIP_SHEET_LANGUAGES.map((lang) => (
                      <DropdownMenuItem
                        key={lang.id}
                        onClick={() => generateParentTipSheet(lang.id)}
                        disabled={generatingTipSheet}
                        className="cursor-pointer"
                        data-testid={`tipsheet-${lang.id}`}
                      >
                        <span className="mr-2">{lang.flag}</span>
                        {lang.label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setEmailTipSheetOpen(true)}
                      className="cursor-pointer"
                      data-testid="email-tipsheet-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email to Parent
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Text Size Controls */}
            <div className="hidden sm:flex items-center gap-1 bg-secondary/30 rounded-full px-2 py-1">
              <ZoomOut className="w-3 h-3 text-text-muted" />
              <div className="flex gap-0.5">
                {TEXT_SIZES.map((size) => (
                  <button
                    key={size.value}
                    onClick={() => setTextSize(size.value)}
                    className={`px-1.5 py-0.5 rounded-full text-xs font-body transition-all ${
                      textSize === size.value
                        ? 'bg-primary text-white'
                        : 'text-text-secondary hover:bg-secondary'
                    }`}
                    data-testid={`text-size-${size.label}`}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
              <ZoomIn className="w-3 h-3 text-text-muted" />
            </div>
            
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-text-secondary hover:text-primary p-2"
              data-testid="home-btn"
            >
              <Home className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Story Title */}
      <div className="px-6 py-4 text-center bg-secondary/20">
        <h1 className="font-heading font-bold text-xl sm:text-2xl text-text-primary">
          {story.title}
        </h1>
        <p className="font-body text-sm text-text-muted">
          A story for {story.student_info?.name}
        </p>
      </div>

      {/* Book Container */}
      <main className="flex-1 px-4 sm:px-6 py-6 sm:py-8 max-w-6xl mx-auto w-full">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-float overflow-hidden book-spine page-shadow">
          <div className="grid lg:grid-cols-2 min-h-[400px] sm:min-h-[500px]">
            {/* Text Side */}
            <div className="p-6 sm:p-10 flex flex-col justify-center bg-gradient-to-br from-background to-background-alt order-2 lg:order-1">
              <div className="flex items-center justify-between mb-6">
                <span className="inline-block px-3 py-1 bg-primary/10 text-primary font-body text-sm rounded-full">
                  Page {currentPage + 1} of {story.pages?.length || 0}
                </span>
                
                {/* Edit Button */}
                {!isEditing ? (
                  <Button
                    onClick={startEditing}
                    variant="ghost"
                    size="sm"
                    className="text-text-muted hover:text-primary"
                    data-testid="edit-text-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      onClick={saveEdit}
                      disabled={savingEdit}
                      size="sm"
                      className="bg-primary text-white hover:bg-primary/90 rounded-full px-3"
                      data-testid="save-edit-btn"
                    >
                      {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      Save
                    </Button>
                    <Button
                      onClick={cancelEditing}
                      variant="ghost"
                      size="sm"
                      className="text-text-muted"
                      data-testid="cancel-edit-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              {isEditing ? (
                <Textarea
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  className="story-text text-xl text-text-primary leading-relaxed min-h-[200px] border-2 border-primary/30 rounded-xl"
                  data-testid="edit-textarea"
                />
              ) : (
                <p className={`story-text ${getCurrentTextSizeClass()} text-text-primary leading-relaxed`} data-testid="story-text">
                  {currentPageData?.text}
                </p>
              )}
            </div>
            
            {/* Image Side */}
            <div className="bg-secondary/30 p-6 sm:p-8 flex items-center justify-center order-1 lg:order-2 min-h-[250px] sm:min-h-0">
              {hasImage ? (
                <img 
                  src={currentPageData.image_base64 || currentPageData.image_url}
                  alt={`Illustration for page ${currentPage + 1}`}
                  className="max-w-full max-h-[300px] sm:max-h-[400px] rounded-2xl shadow-card object-contain"
                  data-testid="story-image"
                />
              ) : (
                <div className="text-center">
                  <div className="w-24 h-24 bg-white/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <ImageIcon className="w-12 h-12 text-primary/30" />
                  </div>
                  <Button
                    onClick={generateImage}
                    disabled={generatingImage}
                    className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-2"
                    data-testid="generate-image-btn"
                  >
                    {generatingImage ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Image
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center items-center gap-4 mt-6">
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
            className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full px-4 sm:px-6 py-2 disabled:opacity-50"
            data-testid="prev-page-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Previous</span>
          </Button>
          
          <div className="flex gap-2">
            {story.pages?.map((_, index) => (
              <button
                key={index}
                onClick={() => handlePageChange(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentPage 
                    ? 'bg-primary scale-125' 
                    : 'bg-secondary hover:bg-primary/50'
                }`}
                data-testid={`page-dot-${index}`}
              />
            ))}
          </div>
          
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === (story.pages?.length || 0) - 1}
            className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full px-4 sm:px-6 py-2 disabled:opacity-50"
            data-testid="next-page-btn"
          >
            <span className="hidden sm:inline">Next</span>
            <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
          </Button>
        </div>
      </main>

      {/* Audio Controls */}
      <div className="bg-white border-t border-secondary px-6 py-4">
        <div className="max-w-3xl mx-auto">
          {/* Background Music Section */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-4 pb-4 border-b border-secondary/50">
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4 text-text-muted" />
              <span className="font-body text-sm text-text-secondary">Background:</span>
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              {BACKGROUND_MUSIC.map((music) => (
                <button
                  key={music.id}
                  onClick={() => handleMusicChange(music.id)}
                  className={`px-3 py-1 rounded-full text-xs font-body transition-all ${
                    selectedMusic === music.id
                      ? 'bg-accent text-white'
                      : 'bg-secondary/50 text-text-secondary hover:bg-secondary'
                  }`}
                  data-testid={`music-${music.id}`}
                >
                  {music.label}
                </button>
              ))}
            </div>
            {selectedMusic !== 'none' && (
              <Button
                onClick={toggleMusic}
                variant="ghost"
                size="sm"
                className={`rounded-full ${musicPlaying ? 'text-accent' : 'text-text-muted'}`}
                data-testid="toggle-music-btn"
              >
                {musicPlaying ? <Music2 className="w-5 h-5 animate-pulse" /> : <Music className="w-5 h-5" />}
              </Button>
            )}
          </div>
          
          {/* Voice & TTS Controls */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Voice Selector */}
            <div className="flex gap-2 flex-wrap justify-center">
              {VOICE_OPTIONS.map((voice) => (
                <button
                  key={voice.value}
                  onClick={() => {
                    setSelectedVoice(voice.value);
                    setAudioSrc(null);
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-body transition-all ${
                    selectedVoice === voice.value
                      ? 'bg-primary text-white'
                      : 'bg-secondary/50 text-text-secondary hover:bg-secondary'
                  }`}
                  title={voice.description}
                  data-testid={`voice-${voice.value}`}
                >
                  {voice.label}
                </button>
              ))}
            </div>
            
            {/* Play Controls */}
            <div className="flex items-center gap-3">
              <Button
                onClick={isPlaying ? pauseAudio : playAudio}
                disabled={loadingAudio}
                className="w-14 h-14 rounded-full bg-accent text-white hover:bg-accent/90 flex items-center justify-center shadow-soft"
                data-testid="play-pause-btn"
              >
                {loadingAudio ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6 ml-1" />
                )}
              </Button>
              
              <Button
                onClick={stopAudio}
                variant="outline"
                className="w-10 h-10 rounded-full border-2 border-text-muted text-text-muted hover:border-primary hover:text-primary"
                data-testid="stop-btn"
              >
                <VolumeX className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          <p className="text-center font-body text-sm text-text-muted mt-3">
            <Volume2 className="w-4 h-4 inline mr-1" />
            Click play to hear this page read aloud
          </p>
        </div>
      </div>

      {/* Hidden Audio Elements */}
      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          onEnded={handleAudioEnded}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      )}
      
      {/* Background Music Audio */}
      <audio ref={musicRef} loop />

      {/* Email Tip Sheet Dialog */}
      <Dialog open={emailTipSheetOpen} onOpenChange={setEmailTipSheetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Email Parent Tip Sheet</DialogTitle>
            <DialogDescription className="font-body">
              Send the tip sheet directly to a parent or caregiver
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-body font-semibold">Recipient Email</Label>
              <Input
                type="email"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                placeholder="parent@example.com"
                className="mt-2"
                data-testid="email-tipsheet-recipient"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Recipient Name (optional)</Label>
              <Input
                value={emailForm.recipient_name}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_name: e.target.value })}
                placeholder="e.g., Mrs. Johnson"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Language</Label>
              <Select value={emailTipSheetLang} onValueChange={setEmailTipSheetLang}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIP_SHEET_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.id} value={lang.id}>
                      {lang.flag} {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-body font-semibold">Personal Message (optional)</Label>
              <Textarea
                value={emailForm.custom_message}
                onChange={(e) => setEmailForm({ ...emailForm, custom_message: e.target.value })}
                placeholder="Add a personal note..."
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailTipSheetOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={sendEmailTipSheet} 
              disabled={sendingEmail}
              className="bg-primary text-white rounded-full"
              data-testid="send-tipsheet-email-btn"
            >
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Desktop Visual Aid Preview Dialog */}
      <Dialog open={desktopAidPreviewOpen} onOpenChange={setDesktopAidPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Desktop Visual Aid Preview
            </DialogTitle>
            <DialogDescription className="font-body">
              Preview and customize the strategy images before downloading
            </DialogDescription>
          </DialogHeader>
          
          {loadingDesktopAid ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
              <p className="text-text-secondary">Generating strategy images...</p>
              <p className="text-xs text-text-muted mt-2">This may take a minute</p>
            </div>
          ) : desktopAidData && (
            <div className="space-y-4">
              {/* Title */}
              <div className="text-center bg-primary/10 rounded-xl p-4">
                <h3 className="font-heading font-bold text-2xl text-primary">
                  {(desktopAidData.card_title || 'MY REMINDERS').toUpperCase()}
                </h3>
                <p className="text-sm text-text-secondary">For: {desktopAidData.student_name}</p>
              </div>
              
              {/* 2x2 Grid of Strategies */}
              <div className="grid grid-cols-2 gap-4">
                {(desktopAidData.strategies || []).slice(0, 4).map((strategy, index) => (
                  <div 
                    key={index} 
                    className="border rounded-xl p-4 bg-white flex flex-col items-center"
                  >
                    {/* Strategy phrase */}
                    <p className="font-heading font-semibold text-sm text-center text-primary mb-3">
                      {strategy.phrase}
                    </p>
                    
                    {/* Image */}
                    <div className="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                      {desktopAidImages[index] ? (
                        <img 
                          src={desktopAidImages[index]} 
                          alt={strategy.phrase}
                          className="w-full h-full object-cover"
                        />
                      ) : regeneratingImage === index ? (
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      ) : (
                        <div className="text-center text-text-muted">
                          <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-xs">Loading...</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Regenerate button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => regenerateStrategyImage(index)}
                      disabled={regeneratingImage !== null}
                      className="mt-3 text-xs"
                      data-testid={`regenerate-image-${index}`}
                    >
                      {regeneratingImage === index ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Sparkles className="w-3 h-3 mr-1" />
                      )}
                      Regenerate Image
                    </Button>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-text-muted text-center">
                Click "Regenerate Image" on any strategy to create a different illustration
              </p>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="ghost" 
              onClick={() => setDesktopAidPreviewOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={downloadDesktopAidPdf}
              disabled={loadingDesktopAid || !desktopAidData}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="download-desktop-aid-pdf"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IEP Goal Generator */}
      <IEPGoalGenerator
        storyId={storyId}
        studentName={story?.student_info?.name || 'Student'}
        scenario={story?.scenario || ''}
        isOpen={iepGoalOpen}
        onClose={() => setIepGoalOpen(false)}
      />
    </div>
  );
};

export default StoryViewer;
